import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash, FiImage, FiX, FiMapPin, FiRefreshCw, FiToggleRight, FiToggleLeft, FiAlertCircle, FiEye, FiTarget, FiChevronLeft, FiChevronRight, FiDownload } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBanners, setTotalBanners] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [previewImage, setPreviewImage] = useState(null);


  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [bannerForm, setBannerForm] = useState({
    title: '',
    description: '',
    type: 'dashboard-banner',
    zoneId: '',
    discountType: '',
    discountValue: ''
  });
  const [zoneForm, setZoneForm] = useState({
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    radius: 5
  });
  const [imageFile, setImageFile] = useState(null);
  const [foregroundImage, setForegroundImage] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null);

  useEffect(() => {
    fetchBanners();
    fetchZones();
  }, [currentPage, itemsPerPage]);

  const fetchBanners = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
        toast.loading('Refreshing banners...', { id: 'refresh' });
      }

      const params = {
        page: currentPage,
        limit: itemsPerPage
      };

      // console.log('Fetching banners with params:', params);
      const response = await api.get('/banners', { params });
      // console.log('Banners response:', response.data);
      
      if (response.data.success) {
        setBanners(response.data.data);
        setTotalPages(response.data.totalPages || 1);
        setTotalBanners(response.data.count || 0);
        
        // Show success message for manual refresh
        if (isManualRefresh) {
          toast.success(`Refreshed! Found ${response.data.data.length} banners`, { id: 'refresh' });
        }
      } else {
        toast.error('Failed to load banners data', { id: 'refresh' });
      }
    } catch (error) {
      console.error('Fetch banners error:', error);
      toast.error(`Failed to fetch banners: ${error.response?.data?.message || error.message}`, { id: 'refresh' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchZones = async () => {
    try {
      const response = await api.get('/banners/zones');
      if (response.data.success) {
        setZones(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch zones');
    }
  };

  const handleCreateZone = async (e) => {
    e.preventDefault();
    try {
      const zoneData = {
        name: zoneForm.name,
        description: zoneForm.description,
        location: {
          latitude: parseFloat(zoneForm.latitude),
          longitude: parseFloat(zoneForm.longitude),
          radius: parseFloat(zoneForm.radius)
        }
      };

      const response = selectedZone
        ? await api.put(`/banners/zones/${selectedZone._id}`, zoneData)
        : await api.post('/banners/zones', zoneData);

      if (response.data.success) {
        toast.success(selectedZone ? 'Zone updated successfully' : 'Zone created successfully');
        setShowZoneModal(false);
        resetZoneForm();
        fetchZones();
      }
    } catch (error) {
      toast.error(selectedZone ? 'Failed to update zone' : 'Failed to create zone');
    }
  };

  const handleDeleteZone = async (zoneId) => {
    if (!window.confirm('Are you sure you want to delete this zone? All banners in this zone will be affected.')) return;
    try {
      await api.delete(`/banners/zones/${zoneId}`);
      toast.success('Zone deleted successfully');
      fetchZones();
    } catch (error) {
      toast.error('Failed to delete zone');
    }
  };

  const openZoneModal = (zone = null) => {
    if (zone) {
      setSelectedZone(zone);
      setZoneForm({
        name: zone.name,
        description: zone.description || '',
        latitude: zone.location?.latitude || '',
        longitude: zone.location?.longitude || '',
        radius: zone.location?.radius || 5
      });
    }
    setShowZoneModal(true);
  };

  const resetZoneForm = () => {
    setZoneForm({ name: '', description: '', latitude: '', longitude: '', radius: 5 });
    setSelectedZone(null);
  };

  const handleCreateBanner = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(bannerForm).forEach(key => {
        if (bannerForm[key]) formData.append(key, bannerForm[key]);
      });
      
      // Handle different image types based on banner type
      if (bannerForm.type === 'dashboard-banner') {
        if (foregroundImage) formData.append('foregroundImage', foregroundImage);
        if (backgroundImage) formData.append('backgroundImage', backgroundImage);
      } else {
        if (imageFile) formData.append('image', imageFile);
      }

      const response = selectedBanner
        ? await api.put(`/banners/${selectedBanner._id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        : await api.post('/banners', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

      if (response.data.success) {
        toast.success(selectedBanner ? 'Banner updated successfully' : 'Banner created successfully');
        setShowBannerModal(false);
        resetBannerForm();
        fetchBanners();
      }
    } catch (error) {
      toast.error('Failed to save banner');
    }
  };

  const toggleBannerStatus = async (bannerId) => {
    try {
      const response = await api.patch(`/banners/${bannerId}/toggle-status`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchBanners();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteBanner = async (bannerId) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;

    try {
      const response = await api.delete(`/banners/${bannerId}`);
      if (response.data.success) {
        toast.success('Banner deleted successfully');
        fetchBanners();
      }
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  const resetBannerForm = () => {
    setBannerForm({
      title: '',
      description: '',
      type: 'dashboard-banner',
      zoneId: '',
      discountType: '',
      discountValue: ''
    });
    setSelectedBanner(null);
    setImageFile(null);
    setForegroundImage(null);
    setBackgroundImage(null);
  };

  const editBanner = (banner) => {
    setSelectedBanner(banner);
    setBannerForm({
      title: banner.title,
      description: banner.description || '',
      type: banner.type,
      zoneId: banner.zoneId._id,
      discountType: banner.discountType || '',
      discountValue: banner.discountValue || ''
    });
    setShowBannerModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-flex items-center justify-center">
            <div className="w-20 h-20 border-4 border-indigo-200 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-indigo-600 rounded-full animate-spin absolute border-t-transparent"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Loading Banners...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                  <FiImage className="text-white text-xl" />
                </div>
                Banner & Popup Management
              </h1>
              <p className="text-gray-600 mt-1">Create and manage banners and popup advertisements</p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold flex items-center gap-2">
                <FiImage className="text-purple-600" />
                {totalBanners} Banners
              </div>
              <button
                onClick={() => {
                  fetchBanners(true);
                  fetchZones();
                }}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                <FiRefreshCw className={`text-sm ${refreshing ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Action Buttons */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6 mb-8">
          <div className="flex flex-wrap gap-4">
            <button 
              onClick={() => {
                resetZoneForm();
                setShowZoneModal(true);
              }}
              className="group relative px-4 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg font-medium hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <FiPlus className="text-sm group-hover:rotate-90 transition-transform duration-200" />
              Create Zone
            </button>
            <button 
              onClick={() => {
                resetBannerForm();
                setShowBannerModal(true);
              }}
              className="group relative px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-medium hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <FiPlus className="text-sm group-hover:rotate-90 transition-transform duration-200" />
              Create Banner
            </button>
          </div>
        </div>

      {/* Zones Section */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
                <FiMapPin className="text-white text-sm" />
              </div>
              Banner Zones (Location-Based)
            </h2>
            <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
              {zones.length} Zones
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {zones.map((zone) => (
              <div key={zone._id} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200 hover:shadow-md transition-all duration-300 hover:border-blue-300 group">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">{zone.name}</h3>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{zone.description || 'No description'}</p>
                  </div>
                  <div className="flex gap-1 ml-3">
                    <button 
                      onClick={() => openZoneModal(zone)} 
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all duration-200 hover:scale-105"
                      title="Edit Zone"
                    >
                      <FiEdit size={16} />
                    </button>
                    <button 
                      onClick={() => handleDeleteZone(zone._id)} 
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all duration-200 hover:scale-105"
                      title="Delete Zone"
                    >
                      <FiTrash size={16} />
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="font-medium text-gray-700">Location:</span>
                    <span className="text-gray-600">{zone.location?.latitude?.toFixed(4) || 'N/A'}, {zone.location?.longitude?.toFixed(4) || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="font-medium text-gray-700">Radius:</span>
                    <span className="text-gray-600">{zone.location?.radius || 0} km</span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Coverage Area</span>
                      <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                        {Math.round(Math.PI * Math.pow(zone.location?.radius || 0, 2))} km²
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {zones.length === 0 && (
              <div className="col-span-full text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiMapPin className="text-gray-400 text-2xl" />
                </div>
                <p className="text-gray-500">No zones created yet</p>
                <p className="text-sm text-gray-400 mt-1">Create your first banner zone to get started</p>
              </div>
            )}
          </div>
        </div>

      {/* Banners Section */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <FiImage className="text-white text-sm" />
                </div>
                Banners & Popups
              </h2>
              <div className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
                {banners.length} Banners
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Image</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Zone</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {banners.map((banner) => (
                  <tr key={banner._id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-300 group">
                    <td className="px-6 py-4">
                      {banner.type === 'dashboard-banner' ? (
                        <div className="flex flex-col gap-2">
                          {banner.foregroundImageUrl && (
                            <div className="relative group/image">
                              <img
                                src={banner.foregroundImageUrl}
                                alt="Foreground"
                                className="w-16 h-8 object-cover rounded-lg cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-md"
                                onClick={() => {
                                  setPreviewImage(banner.foregroundImageUrl)
                                }}
                              />
                            </div>
                          )}
                          {banner.backgroundImageUrl && (
                            <div className="relative group/image">
                              <img
                                src={banner.backgroundImageUrl}
                                alt="Background"
                                className="w-16 h-8 object-cover rounded-lg cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-md opacity-70"
                                onClick={() => setPreviewImage(banner.backgroundImageUrl)}
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        banner.imageUrl && (
                          <div className="relative group/image">
                            <img
                              src={banner.imageUrl}
                              alt={banner.title}
                              className="w-16 h-10 object-cover rounded-lg cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-md"
                              onClick={() => setPreviewImage(banner.imageUrl)}
                            />
                          </div>
                        )
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-900 group-hover:text-purple-600 transition-colors duration-200">{banner.title || 'Untitled'}</div>
                      {banner.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{banner.description}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 w-fit ${
                        banner.type === 'dashboard-banner' ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300' : 
                        banner.type === 'internal-banner' ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300' : 
                        'bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 border border-amber-300'
                      }`}>
                        {banner.type === 'dashboard-banner' && <FiImage className="text-xs" />}
                        {banner.type === 'internal-banner' && <FiTarget className="text-xs" />}
                        {banner.type === 'dashboard-popup' && <FiAlertCircle className="text-xs" />}
                        {banner.type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        <span className="text-gray-700 font-medium">{banner.zoneId?.name || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${banner.isActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          banner.isActive ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300' : 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300'
                        }`}>
                          {banner.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => editBanner(banner)} 
                          className="group relative p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all duration-200 hover:scale-110 hover:shadow-md"
                          title="Edit Banner"
                        >
                          <FiEdit size={16} className="group-hover:rotate-12 transition-transform" />
                        </button>
                        <button 
                          onClick={() => toggleBannerStatus(banner._id)}
                          className={`group relative p-2 rounded-lg transition-all duration-200 hover:scale-110 hover:shadow-md ${
                            banner.isActive 
                              ? 'text-amber-600 hover:bg-amber-100' 
                              : 'text-green-600 hover:bg-green-100'
                          }`}
                          title={banner.isActive ? 'Deactivate Banner' : 'Activate Banner'}
                        >
                          {banner.isActive ? <FiToggleRight size={16} className="group-hover:rotate-12 transition-transform" /> : <FiToggleLeft size={16} className="group-hover:rotate-12 transition-transform" />}
                        </button>
                        <button 
                          onClick={() => deleteBanner(banner._id)} 
                          className="group relative p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all duration-200 hover:scale-110 hover:shadow-md"
                          title="Delete Banner"
                        >
                          <FiTrash size={16} className="group-hover:scale-110 transition-transform" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        {/* Enhanced Empty State */}
        {banners.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiImage className="text-purple-400 text-3xl" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No banners created yet</h3>
            <p className="text-gray-500 mb-6">Create your first banner to start displaying advertisements</p>
            <button 
              onClick={() => {
                resetBannerForm();
                setShowBannerModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors duration-200"
            >
              <FiPlus className="text-sm" />
              Create First Banner
            </button>
          </div>
        )}

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mt-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-semibold text-gray-900">{banners.length}</span> of <span className="font-semibold text-gray-900">{totalBanners}</span> banners
                </div>
                <div className="h-6 w-px bg-gray-300"></div>
                <div className="text-sm text-gray-600">
                  Page <span className="font-semibold text-gray-900">{currentPage}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg text-sm font-medium hover:from-gray-600 hover:to-gray-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
                  >
                    <FiChevronLeft className="text-sm" />
                    Previous
                  </button>
                  
                  {[...Array(totalPages)].map((_, idx) => {
                    const pageNum = idx + 1;
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            pageNum === currentPage 
                              ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md transform scale-105' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                      return <span key={pageNum} className="px-2 text-gray-500">...</span>;
                    }
                    return null;
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg text-sm font-medium hover:from-gray-600 hover:to-gray-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
                  >
                    Next
                    <FiChevronRight className="text-sm" />
                  </button>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-gray-600">per page</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Zone Modal */}
      {showZoneModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                {selectedZone ? 'Edit Banner Zone' : 'Create Banner Zone'}
              </h2>
              <button 
                onClick={() => {
                  setShowZoneModal(false);
                  resetZoneForm();
                }}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                <FiX size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateZone}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Zone Name *</label>
                  <input
                    type="text"
                    value={zoneForm.name}
                    onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={zoneForm.description}
                    onChange={(e) => setZoneForm({ ...zoneForm, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-vertical"
                    rows="3"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Latitude *</label>
                    <input
                      type="number"
                      step="any"
                      value={zoneForm.latitude}
                      onChange={(e) => setZoneForm({ ...zoneForm, latitude: e.target.value })}
                      placeholder="e.g., 28.7041"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Longitude *</label>
                    <input
                      type="number"
                      step="any"
                      value={zoneForm.longitude}
                      onChange={(e) => setZoneForm({ ...zoneForm, longitude: e.target.value })}
                      placeholder="e.g., 77.1025"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Radius (km) *</label>
                  <input
                    type="number"
                    step="0.1"
                    value={zoneForm.radius}
                    onChange={(e) => setZoneForm({ ...zoneForm, radius: e.target.value })}
                    min="0.1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    required
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    Banners will be shown to users within this radius from the specified location
                  </p>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex gap-3">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowZoneModal(false);
                    resetZoneForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors duration-200"
                >
                  {selectedZone ? 'Update Zone' : 'Create Zone'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Banner Modal */}
      {showBannerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                {selectedBanner ? 'Edit Banner' : 'Create Banner'}
              </h2>
              <button 
                onClick={() => setShowBannerModal(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                <FiX size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateBanner}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={bannerForm.title}
                    onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={bannerForm.description}
                    onChange={(e) => setBannerForm({ ...bannerForm, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-vertical"
                    rows="3"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                    <select
                      value={bannerForm.type}
                      onChange={(e) => setBannerForm({ ...bannerForm, type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      required
                    >
                      <option value="dashboard-banner">Dashboard Banner</option>
                      <option value="internal-banner">Internal Banner</option>
                      <option value="dashboard-popup">Dashboard Popup</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Zone *</label>
                    <select
                      value={bannerForm.zoneId}
                      onChange={(e) => setBannerForm({ ...bannerForm, zoneId: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      required
                    >
                      <option value="">Select Zone</option>
                      {zones.map((zone) => (
                        <option key={zone._id} value={zone._id}>
                          {zone.name} ({zone.location?.radius} km radius)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Conditional Fields Based on Type */}
                {bannerForm.type === 'dashboard-banner' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Foreground Image</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setForegroundImage(e.target.files[0])}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <p className="text-sm text-gray-600 mt-1">Main promotional image</p>
                      {selectedBanner && selectedBanner.foregroundImageUrl && (
                        <div className="mt-2">
                          <img 
                            src={selectedBanner.foregroundImageUrl} 
                            alt="Current Foreground" 
                            className="max-w-[200px] max-h-[100px] object-contain border border-gray-300 rounded p-1"
                          />
                          <p className="text-xs text-gray-600 mt-1">Current foreground image</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Background Image *</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setBackgroundImage(e.target.files[0])}
                        required={!selectedBanner}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <p className="text-sm text-gray-600 mt-1">Background design image</p>
                      {selectedBanner && selectedBanner.backgroundImageUrl && (
                        <div className="mt-2">
                          <img 
                            src={selectedBanner.backgroundImageUrl} 
                            alt="Current Background" 
                            className="max-w-[200px] max-h-[100px] object-contain border border-gray-300 rounded p-1"
                          />
                          <p className="text-xs text-gray-600 mt-1">Current background image</p>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Discount Type</label>
                        <select
                          value={bannerForm.discountType}
                          onChange={(e) => setBannerForm({ ...bannerForm, discountType: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="">No Discount</option>
                          <option value="percentage">Percentage (%)</option>
                          <option value="flat">Flat Amount (₹)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Discount Value</label>
                        <input
                          type="number"
                          value={bannerForm.discountValue}
                          onChange={(e) => setBannerForm({ ...bannerForm, discountValue: e.target.value })}
                          placeholder={bannerForm.discountType === 'percentage' ? 'e.g., 20' : 'e.g., 100'}
                          disabled={!bannerForm.discountType}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Banner Image *</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setImageFile(e.target.files[0])}
                      required={!selectedBanner}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                    {selectedBanner && selectedBanner.imageUrl && (
                      <div className="mt-2">
                        <img 
                          src={selectedBanner.imageUrl} 
                          alt="Current Banner" 
                          className="max-w-[300px] max-h-[150px] object-contain border border-gray-300 rounded p-1"
                        />
                        <p className="text-xs text-gray-600 mt-1">Current banner image</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="p-6 border-t border-gray-200 flex gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowBannerModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors duration-200"
                >
                  {selectedBanner ? 'Update Banner' : 'Create Banner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {previewImage && (
  <div
    className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4"
    onClick={() => setPreviewImage(null)}
  >
    <div
      className="relative w-full max-w-6xl max-h-[92vh] flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-4 text-white">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
            <FiImage className="text-lg" />
          </div>
          <div>
            <h3 className="font-semibold">Image Preview</h3>
            <p className="text-xs text-gray-300">
              Click outside or press close to exit
            </p>
          </div>
        </div>

        <button
          onClick={() => setPreviewImage(null)}
          className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition"
        >
          <FiX className="text-lg" />
        </button>
      </div>

      {/* Image Viewer */}
      <div className="relative flex-1 bg-black/40 rounded-2xl overflow-hidden flex items-center justify-center shadow-2xl">
        <img
          src={previewImage}
          alt="Preview"
          className="max-w-full max-h-[80vh] object-contain select-none"
        />

        {/* Floating Actions */}
        <div className="absolute bottom-5 right-5 flex gap-3">
          <button
            onClick={() => {
              const link = document.createElement("a");
              link.href = previewImage;
              link.download = "image.jpg";
              link.target = "_blank";
              link.click();
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 shadow-lg transition"
          >
            <FiDownload className="text-sm" />
            Download
          </button>

          <button
            onClick={() => window.open(previewImage, "_blank")}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg text-sm font-medium flex items-center gap-2 shadow-lg transition"
          >
            <FiEye className="text-sm" />
            Open
          </button>
        </div>
      </div>
    </div>
  </div>
)}

      </div>
    </div>
  );
};

export default BannerManagement;
